import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Send, Square, Maximize2, Plus, Bookmark, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import mikoCharacter from "@/assets/miko-character.png";
import { TypingMarkdown } from "@/components/student/TypingMarkdown";
import type { Message } from "@/hooks/useMikoConversations";
import { DEFAULT_GREETING } from "@/hooks/useMikoConversations";
import { useLearningAreaContext } from "@/hooks/useLearningAreaContext";
import { useMikoSuggestions } from "@/hooks/useMikoSuggestions";
import { useMitscrollen } from "@/hooks/useMitscrollen";
import { UpgradeDialog } from "@/components/student/UpgradeDialog";

const BUBBLE_GREETING = `Hallo 👋 Ich bin Miko! Wie kann ich dir helfen?`;

export function MikoFloatingBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: BUBBLE_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [savedMessageIndices, setSavedMessageIndices] = useState<Set<number>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);
  const verlaufRef = useRef<HTMLDivElement>(null);
  const [limitOffen, setLimitOffen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const learningAreaContext = useLearningAreaContext();
  const suggestions = useMikoSuggestions();

  // Hide on MiKo chat page (full page already open), on the dashboard root
  // (Dashboard hat eigenen Miko-Einstieg) und auf mobile (Bottom-Nav hat Miko-Link).
  const isMikoChatPage = location.pathname.startsWith("/mein-bereich/miko");
  const isDashboardRoot = location.pathname === "/mein-bereich";

  // Mitscrollen bis zum Ende der Antwort, auch während die Schrift nachtippt.
  useMitscrollen({ bereich: verlaufRef, neuStart: messages.filter((m) => m.role === "user").length, aktiv: isOpen });

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (isMikoChatPage || isDashboardRoot || isMobile) return null;

  const handleStop = () => {
    abortControllerRef.current?.abort();
  };

  const handleNewChat = () => {
    setMessages([{ role: "assistant", content: BUBBLE_GREETING }]);
    setConversationId(null);
    setSavedMessageIndices(new Set());
    setInput("");
  };

  const saveConversation = async (msgs: Message[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const user = session.user;

      const title = msgs.find(m => m.role === 'user')?.content.slice(0, 50) || 'Bubble Chat';

      if (conversationId) {
        await supabase
          .from('chat_conversations')
          .update({ messages: msgs as any, updated_at: new Date().toISOString() })
          .eq('id', conversationId);
      } else {
        const { data } = await supabase
          .from('chat_conversations')
          .insert({
            user_id: user.id,
            role: 'allgemein',
            title,
            messages: msgs as any,
          })
          .select('id')
          .single();
        if (data) setConversationId(data.id);
      }
    } catch (err) {
      console.error('Error saving bubble conversation:', err);
    }
  };

  const handleSaveNote = async (content: string, messageIndex: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({ title: "Nicht angemeldet", variant: "destructive" });
        return;
      }

      const { error } = await supabase.from("miko_notes").insert({
        user_id: session.user.id,
        content,
        source_conversation_id: conversationId,
      });

      if (error) throw error;

      setSavedMessageIndices(prev => new Set(prev).add(messageIndex));
      toast({ title: "Als Miko-Notiz gespeichert ✨" });
    } catch (err) {
      console.error("Error saving miko note:", err);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    }
  };

  const handleSend = async (overrideInput?: string) => {
    const text = overrideInput ?? input;
    if (!text.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/miko-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({
            messages: messages
              .filter(
                (m) =>
                  !(m.role === "assistant" && m.content === BUBBLE_GREETING)
              )
              .concat(userMessage),
            includeContent: true,
            ...(learningAreaContext ? { learningAreaContext } : {}),
          }),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        if (response.status === 402) {
          // Kontingent aufgebraucht: Fenster zur Mitgliedschaft, die Frage bleibt im Feld.
          setMessages((prev) => prev.slice(0, -1));
          setInput(text);
          setLimitOffen(true);
          setIsLoading(false);
          return;
        }
        if (response.status === 401) {
          toast({
            title: "Bitte melde dich erneut an",
            description: "Deine Anmeldung ist abgelaufen.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        if (response.status === 429) {
          toast({
            title: "Gerade sind es zu viele Anfragen",
            description: "Bitte versuche es in einer Minute noch einmal.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        toast({
          title: "Fehler",
          description: "Nachricht konnte nicht gesendet werden.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      if (!response.body) throw new Error("No body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";
      let textBuffer = "";

      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });
        let newlineIndex: number;

        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantMessage += content;
              setMessages((prev) => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;
                if (lastIdx >= 0 && updated[lastIdx].role === "assistant") {
                  updated[lastIdx] = {
                    ...updated[lastIdx],
                    content: updated[lastIdx].content + content,
                  };
                }
                return updated;
              });
            }
          } catch {
            // Incomplete JSON
          }
        }
      }

      // Save conversation
      const finalMessages = [...newMessages, { role: 'assistant' as const, content: assistantMessage }];
      await saveConversation(finalMessages);
    } catch (error: any) {
      if (error.name === "AbortError") {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && !last.content) {
            return prev.slice(0, -1);
          }
          return prev;
        });
      } else {
        console.error("Bubble chat error:", error);
        toast({
          title: "Fehler",
          description: "Nachricht konnte nicht gesendet werden.",
          variant: "destructive",
        });
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openFullChat = () => {
    setIsOpen(false);
    const path = conversationId 
      ? `/mein-bereich/miko/chat?conversation=${conversationId}`
      : "/mein-bereich/miko/chat";
    navigate(path);
  };

  const showSuggestions = messages.length === 1 && messages[0].role === "assistant" && !isLoading;

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[460px] max-h-[620px] rounded-2xl shadow-2xl border border-border bg-background flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-primary/10 to-accent/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/20 p-0.5">
                <img
                  src={mikoCharacter}
                  alt="Miko"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Miko</h4>
                <p className="text-[10px] text-muted-foreground">
                  {learningAreaContext ? `Lern-Coach · ${learningAreaContext.areaTitle}` : "Dein KI-Assistent"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="h-7 w-7 p-0"
                title="Neuer Chat"
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={openFullChat}
                className="h-7 w-7 p-0"
                title="Im Vollbild öffnen"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={verlaufRef} className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0 max-h-[460px]">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={cn(
                  "flex",
                  msg.role === "user" ? "justify-end" : "justify-start"
                )}
              >
                <div className={cn("max-w-[85%]", msg.role === "assistant" ? "group/msg" : "")}>
                  <div
                    className={cn(
                      "rounded-2xl px-3 py-2 text-sm",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    )}
                  >
                    {msg.role === "assistant" ? (
                      <TypingMarkdown
                        content={msg.content}
                        isStreaming={isLoading && i === messages.length - 1}
                      />
                    ) : (
                      msg.content
                    )}
                  </div>
                  {/* Bookmark button for assistant messages (not greeting, not empty, not streaming) */}
                  {msg.role === "assistant" && msg.content && msg.content !== BUBBLE_GREETING && !(isLoading && i === messages.length - 1) && (
                    <div className="flex justify-end mt-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleSaveNote(msg.content, i)}
                        disabled={savedMessageIndices.has(i)}
                        className={cn(
                          "flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md transition-colors",
                          savedMessageIndices.has(i)
                            ? "text-primary cursor-default"
                            : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                        )}
                        title={savedMessageIndices.has(i) ? "Gespeichert" : "Als Notiz speichern"}
                      >
                        <Bookmark className={cn("w-3 h-3", savedMessageIndices.has(i) && "fill-primary")} />
                        {savedMessageIndices.has(i) ? "Gespeichert" : "Merken"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading &&
              messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-md px-3 py-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}
            
            {/* Suggestions */}
            {showSuggestions && (
              <div className="flex flex-wrap gap-1.5 pt-2">
                {suggestions.slice(0, 3).map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(suggestion)}
                    className="text-left px-2.5 py-1.5 rounded-lg text-[11px] leading-tight text-muted-foreground bg-muted/40 hover:bg-muted hover:text-foreground transition-colors border border-border/50 hover:border-border"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

          </div>

          {/* Input */}
          <div className="border-t p-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Schreib Miko eine Nachricht..."
                rows={1}
                className="flex-1 resize-none bg-muted/50 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 max-h-20"
              />
              {isLoading ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleStop}
                  className="h-8 w-8 p-0 shrink-0"
                >
                  <Square className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleSend()}
                  disabled={!input.trim()}
                  className="h-8 w-8 p-0 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Bubble Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            "fixed bottom-6 right-6 z-50",
            "w-14 h-14 rounded-full",
            "bg-gradient-to-br from-primary to-accent",
            "shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40",
            "hover:scale-105 active:scale-95 transition-all duration-200",
            "flex items-center justify-center",
            "group"
          )}
          title="Mit Miko chatten"
        >
          <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-background/30">
            <img
              src={mikoCharacter}
              alt="Miko"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Pulse animation */}
          <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping opacity-20" />
        </button>
      )}
      <UpgradeDialog open={limitOffen} onOpenChange={setLimitOffen} anlass="miko" />
    </>
  );
}
